import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getGoogleWorkspaceStatus,
  getGoogleWorkspaceAuthUrl,
  disconnectGoogleWorkspace,
  getCompanyIntegrations,
  getCompanyIntegrationsHealth,
  getIntegrationHealth,
  updateGoogleWorkspaceConnection,
  setGoogleWorkspaceSyncPreference,
} from '../api';
import { IntegrationLogo } from '../components/Integrations/IntegrationsGrid';
import RequestDrawer from '../components/Integrations/RequestDrawer';
import { getHealthBadgeInfo } from '../components/Integrations/integrationHealthUtils';
import {
  IntegrationAlertBanner,
  IntegrationActionButtons,
} from '../components/Integrations/IntegrationSharedComponents';
import { Button, TableSkeleton, Switch } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import {
  DashboardPage,
  DashboardCard,
  DashboardTableWrapper,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
} from '../components/PremiumDesign';
import { openGoogleAuthWindow } from '../crm/utils/googleWorkspaceUtils';

const GW_CALLBACK_URL = `${window.location.origin}/integrations/google-workspace/callback`;

// Scope label mapping – human-readable labels for granted scopes
const SCOPE_LABELS = [
  {
    key: 'spreadsheets',
    label: 'Google Sheets',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8 shrink-0" fill="currentColor">
        <path
          fill="#34A853"
          d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 14H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2z"
        />
      </svg>
    ),
    color: 'bg-green-50 text-green-700 border-green-200',
  },
  {
    key: 'calendar',
    label: 'Google Calendar',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8 shrink-0" fill="currentColor">
        <path
          fill="#4285F4"
          d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM7 11h5v5H7z"
        />
      </svg>
    ),
    color: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  {
    key: 'drive.file',
    label: 'Google Drive',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8 shrink-0" fill="currentColor">
        <path fill="#FBBC05" d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z" />
        <path fill="#4285F4" d="M22.85 15L16.29 3.5H9.15L15.71 15z" />
        <path fill="#34A853" d="M4.58 21h14.84l3.43-6H1.15z" />
      </svg>
    ),
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  {
    key: 'documents',
    label: 'Google Docs',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8 shrink-0" fill="currentColor">
        <path
          fill="#4285F4"
          d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
        />
      </svg>
    ),
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
];

const FEATURE_CARDS = [
  {
    scopeKey: 'spreadsheets',
    title: 'Google Sheets',
    desc: 'Export CRM leads and contacts directly to Google Sheets for analysis or sharing.',
    bg: 'bg-green-50 border-green-100',
  },
  {
    scopeKey: 'calendar',
    title: 'Calendar Sync',
    desc: 'Automatically sync appointment bookings to Google Calendar for your team.',
    bg: 'bg-blue-50 border-blue-100',
  },
  {
    scopeKey: 'drive.file',
    title: 'Google Drive',
    desc: 'Attach Drive files to CRM records and auto-upload generated documents.',
    bg: 'bg-yellow-50 border-yellow-100',
  },
  {
    scopeKey: 'documents',
    title: 'Google Docs',
    desc: 'Create and attach Docs to leads, quotations, and customer profiles.',
    bg: 'bg-indigo-50 border-indigo-100',
  },
];

function getCompanyId() {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      return userObj?.company_id || localStorage.getItem('company_id');
    }
  } catch {
    // ignore parse errors
  }
  return localStorage.getItem('company_id');
}

// Disconnect Confirmation Modal
function DisconnectModal({ opened, onClose, onConfirm, isLoading }) {
  useEffect(() => {
    if (!opened) return undefined;
    const onKey = (event) => {
      if (isLoading) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        onConfirm();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [opened, isLoading, onClose, onConfirm]);

  if (!opened) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gw-disconnect-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={isLoading ? undefined : onClose}
      />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
            <svg
              className="w-5 h-5 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>
          <div>
            <h3
              id="gw-disconnect-title"
              className="text-[15px] font-semibold text-gray-900 leading-tight"
            >
              Disconnect Google Workspace
            </h3>
            <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
              This will revoke access to{' '}
              <strong className="text-gray-700">Google Sheets</strong>,{' '}
              <strong className="text-gray-700">Calendar</strong>, and{' '}
              <strong className="text-gray-700">Drive &amp; Docs sync</strong>{' '}
              for this company. You can reconnect at any time.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2.5 pt-1 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            id="gw-disconnect-confirm-btn"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-[13px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-60 flex items-center gap-2 shadow-sm"
          >
            {isLoading && (
              <svg
                className="w-3.5 h-3.5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                <path d="M12 3a9 9 0 0 1 9 9" />
              </svg>
            )}
            {isLoading ? 'Disconnecting...' : 'Disconnect'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sync Preference Modal ─────────────────────────────────────────────────────
function SyncPreferenceModal({ opened, onClose, onConfirm }) {
  const [selected, setSelected] = useState('full_sync');

  useEffect(() => {
    if (!opened) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [opened, onClose]);

  if (!opened) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gw-sync-pref-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#e8f0fe] flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-6 h-6">
              <path
                fill="#4285F4"
                d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 3.25l-8 5-8-5V6l8 5 8-5v1.25z"
              />
            </svg>
          </div>
          <div>
            <h3
              id="gw-sync-pref-title"
              className="text-[15px] font-semibold text-gray-900 leading-tight"
            >
              How would you like to set up Gmail?
            </h3>
            <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
              Choose how existing emails are handled when you connect.
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="flex flex-col gap-3">
          {[
            {
              value: 'full_sync',
              title: 'Import existing emails',
              desc: 'Sync your last 6 months of Gmail conversations into the CRM.',
              icon: '📥',
            },
            {
              value: 'start_fresh',
              title: 'Start fresh',
              desc: 'Only new emails received from now on will appear in the CRM.',
              icon: '✨',
            },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selected === opt.value
                  ? 'border-[#4285F4] bg-[#e8f0fe]/40'
                  : 'border-gray-100 bg-gray-50 hover:border-gray-200'
              }`}
            >
              <input
                type="radio"
                name="sync_preference"
                value={opt.value}
                checked={selected === opt.value}
                onChange={() => setSelected(opt.value)}
                className="mt-0.5 accent-[#4285F4]"
              />
              <div>
                <span className="text-[13px] font-semibold text-gray-900">
                  {opt.icon} {opt.title}
                </span>
                <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">
                  {opt.desc}
                </p>
              </div>
            </label>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2.5 pt-1 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            id="gw-sync-pref-confirm-btn"
            onClick={() => onConfirm(selected)}
            className="px-5 py-2 text-[13px] font-semibold text-white bg-[#4285F4] hover:bg-[#3367d6] rounded-lg transition-colors shadow-sm"
          >
            Save & Start Sync
          </button>
        </div>
      </div>
    </div>
  );
}

export function GoogleWorkspaceDetailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  // Track which toggles were just enabled and await a sync-existing decision
  // Values: null | 'invoices' | 'quotations'
  const [syncPendingFor, setSyncPendingFor] = useState(null);
  const [isSyncingExisting, setIsSyncingExisting] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);

  // 1. Connection status query (stores google_email and granted scopes)
  const {
    data: statusData,
    isLoading: isStatusLoading,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['google-workspace-status', companyId],
    queryFn: () => getGoogleWorkspaceStatus(companyId),
    enabled: !!companyId,
    retry: false,
  });

  // Watch for pending sync preference
  useEffect(() => {
    if (statusData?.sync_preference === 'pending') {
      setSyncModalOpen(true);
    }
  }, [statusData?.sync_preference]);

  // 2. Company integrations catalog query (for companyIntegrationId)
  const { data: companyIntegrations = [] } = useQuery({
    queryKey: ['company-integrations', companyId],
    queryFn: () => getCompanyIntegrations(companyId),
    enabled: !!companyId,
  });

  // 3. Health list query (FE Source of Truth for live health status)
  // GET /api/v1/integrations/companies/{companyId}/health/
  const {
    data: healthListRaw,
    isLoading: isHealthListLoading,
    isError: isHealthError,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['company-integrations-health', companyId],
    queryFn: () => getCompanyIntegrationsHealth(companyId),
    enabled: !!companyId,
  });

  useEffect(() => {
    const refreshAll = () => {
      queryClient.invalidateQueries({
        queryKey: ['google-workspace-status', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['company-integrations', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['company-integrations-health', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['integration-health-detail', companyId],
      });
      refetchStatus();
      refetchHealth();
    };

    const onAuthDone = (event) => {
      if (event.data?.type !== 'gw-auth-done') return;
      if (event.data.status === 'error') {
        toast.error(event.data.message || 'Google sign-in did not finish.');
        return;
      }
      if (event.data.status !== 'success') return;
      refreshAll();
    };

    const onFocus = () => {
      // Whenever the user returns to this window, refresh integration status
      refreshAll();
    };

    const onStorage = (e) => {
      if (e.key === 'gw_auth_event') {
        refreshAll();
      }
    };

    window.addEventListener('message', onAuthDone);
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('message', onAuthDone);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, [refetchStatus, refetchHealth, queryClient, companyId]);

  const activeIntegration = Array.isArray(companyIntegrations)
    ? companyIntegrations.find(
        (item) => item.integration_slug === 'google_workspace',
      )
    : null;

  const googleHealthFromList = Array.isArray(healthListRaw)
    ? healthListRaw.find((item) => item.integration_slug === 'google_workspace')
    : null;

  const companyIntegrationId =
    activeIntegration?.id || googleHealthFromList?.id;

  // 4. Single health query for this integration if ID known
  // GET /api/v1/integrations/companies/{companyId}/{companyIntegrationId}/health/
  const { data: singleHealthData } = useQuery({
    queryKey: ['integration-health-detail', companyId, companyIntegrationId],
    queryFn: () => getIntegrationHealth({ companyId, companyIntegrationId }),
    enabled: !!companyId && !!companyIntegrationId,
  });

  const isHealthSkeleton = useDelayedSkeleton(
    isHealthListLoading && !!healthListRaw,
  );
  const isPageLoading =
    !!companyId &&
    ((isStatusLoading && statusData === undefined) ||
      (isHealthListLoading && healthListRaw === undefined));

  const isConnected =
    statusData?.is_connected ?? !!activeIntegration?.is_connected;

  // Resolve health item from single query or list snapshot
  const activeHealthItem = singleHealthData ||
    googleHealthFromList || {
      id: companyIntegrationId || null,
      integration_name: 'Google Workspace',
      integration_slug: 'google_workspace',
      lifecycle_status: isConnected ? 'activated' : 'disconnected',
      lifecycle_status_label: isConnected
        ? 'Account Activated'
        : 'Disconnected',
      health_status: isConnected ? null : 'disconnected',
      health_status_label: isConnected ? 'Not checked yet' : 'Not Connected',
      connected_account_name: isConnected
        ? statusData?.google_email || null
        : null,
      connected_workspace_name: null,
      failure_reason: null,
    };

  // Derive health badge info based on prompt rules
  const badgeInfo = getHealthBadgeInfo(
    activeHealthItem?.health_status,
    activeHealthItem?.health_status_label,
    isConnected,
  );

  const isHealthy = badgeInfo.isHealthy;
  const isUnhealthy =
    !isHealthy && (isConnected || !!activeHealthItem?.health_status);

  // Email displayed: only when connected; priority from health item (connected_account_name), fallback to connection google_email
  const displayEmail = isConnected
    ? activeHealthItem?.connected_account_name ||
      statusData?.google_email ||
      null
    : null;

  const workspaceName = (
    activeHealthItem?.connected_workspace_name || ''
  ).trim();
  const showWorkspace =
    isConnected &&
    !!workspaceName &&
    workspaceName.toLowerCase() !==
      String(displayEmail || '')
        .trim()
        .toLowerCase();

  const scopes = statusData?.scopes || [];

  // Reconnect / Disconnect mutation
  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationFn: async () => {
      try {
        await updateGoogleWorkspaceConnection({
          companyId,
          payload: {
            save_invoices_to_drive: false,
            save_quotations_to_drive: false,
            save_lead_attachments_to_drive: false,
          },
        });
      } catch (err) {
        console.warn('Could not reset drive options before disconnect:', err);
      }
      return disconnectGoogleWorkspace(companyId);
    },
    onSuccess: async () => {
      toast.success('Google Workspace disconnected successfully.');
      queryClient.setQueryData(
        ['google-workspace-status', companyId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            is_connected: false,
            save_invoices_to_drive: false,
            save_quotations_to_drive: false,
            save_lead_attachments_to_drive: false,
          };
        },
      );
      setSyncPendingFor(null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['google-workspace-status', companyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['company-integrations', companyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['company-integrations-health', companyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['integration-health'],
        }),
      ]);
      refetchStatus();
      refetchHealth();
      setDisconnectOpen(false);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Failed to disconnect. Please try again.',
      );
    },
  });

  const { mutate: updateSettings, isPending: isUpdatingSettings } = useMutation(
    {
      mutationFn: (payload) =>
        updateGoogleWorkspaceConnection({ companyId, payload }),
      onSuccess: (_data, payload) => {
        if (payload?.sync_existing_invoices) {
          toast.success(
            'Background sync started — past invoices will upload to Drive shortly.',
          );
        } else if (payload?.sync_existing_quotations) {
          toast.success(
            'Background sync started — past quotations will upload to Drive shortly.',
          );
        } else {
          toast.success('Drive settings updated successfully.');
        }
        queryClient.invalidateQueries({
          queryKey: ['google-workspace-status', companyId],
        });
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.detail ||
            err?.response?.data?.message ||
            'Failed to update Drive settings.',
        );
      },
    },
  );

  const handleToggle = (key, value) => {
    updateSettings({ [key]: value });
    // Show the sync-existing inline banner when a relevant toggle is turned ON
    if (value === true && key === 'save_invoices_to_drive') {
      setSyncPendingFor('invoices');
    } else if (value === true && key === 'save_quotations_to_drive') {
      setSyncPendingFor('quotations');
    } else if (
      key === 'save_invoices_to_drive' ||
      key === 'save_quotations_to_drive'
    ) {
      // Toggle was turned OFF — dismiss any pending banner
      if (
        (key === 'save_invoices_to_drive' && syncPendingFor === 'invoices') ||
        (key === 'save_quotations_to_drive' && syncPendingFor === 'quotations')
      ) {
        setSyncPendingFor(null);
      }
    }
  };

  const handleSyncExisting = (type, shouldSync) => {
    if (!shouldSync) {
      setSyncPendingFor(null);
      return;
    }
    setIsSyncingExisting(true);
    const payload =
      type === 'invoices'
        ? { sync_existing_invoices: true }
        : { sync_existing_quotations: true };
    updateSettings(payload, {
      onSettled: () => {
        setIsSyncingExisting(false);
        setSyncPendingFor(null);
      },
    });
  };

  const { mutate: setSyncPref } = useMutation({
    mutationFn: (preference) =>
      setGoogleWorkspaceSyncPreference({ companyId, preference }),
    onSuccess: () => {
      toast.success('Sync preferences saved.');
      refetchStatus();
      setSyncModalOpen(false);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Failed to save sync preferences.',
      );
    },
  });

  const handleConnect = async () => {
    if (!companyId) {
      toast.error('Company ID is missing.');
      return;
    }

    setIsConnecting(true);
    try {
      const res = await getGoogleWorkspaceAuthUrl(companyId, GW_CALLBACK_URL);
      let authUrl = res?.auth_url ?? res?.url;
      if (!authUrl) {
        toast.error('Could not retrieve Google authorization URL.');
        setIsConnecting(false);
        return;
      }

      sessionStorage.setItem('gw_company_id', String(companyId));
      localStorage.setItem('gw_company_id', String(companyId));
      const popup = openGoogleAuthWindow(authUrl);
      if (popup) {
        const timer = setInterval(() => {
          if (popup.closed) {
            clearInterval(timer);
            queryClient.invalidateQueries({
              queryKey: ['google-workspace-status', companyId],
            });
            queryClient.invalidateQueries({
              queryKey: ['company-integrations', companyId],
            });
            queryClient.invalidateQueries({
              queryKey: ['company-integrations-health', companyId],
            });
            refetchStatus();
            refetchHealth();
          }
        }, 800);
      }
      setIsConnecting(false);
    } catch (err) {
      toast.error(
        err?.response?.status === 403
          ? 'You do not have permission to connect Google Workspace.'
          : err?.response?.data?.detail ||
              err?.response?.data?.message ||
              'Failed to initiate Google sign-in.',
      );
      setIsConnecting(false);
    }
  };

  const appForDrawer = {
    id: 'google_workspace',
    name: 'Google Workspace',
    logoType: 'google_workspace',
    description:
      'Connect Google Workspace to export leads to Sheets, sync Calendar bookings, and attach Drive & Docs.',
  };

  return (
    <>
      <DisconnectModal
        opened={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={() => disconnect()}
        isLoading={isDisconnecting}
      />
      <SyncPreferenceModal
        opened={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        onConfirm={(pref) => setSyncPref(pref)}
      />

      <DashboardPage>
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              id="gw-back-btn"
              onClick={() => navigate('/integrations')}
              className="w-8 h-8 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700 flex items-center justify-center transition-colors focus:outline-none shrink-0 bg-white shadow-sm"
              aria-label="Back to Integrations"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="min-w-0 text-lg font-semibold tracking-tight text-gray-900 sm:text-[22px]">
              Google Workspace Details
            </h1>
          </div>
        </div>

        {/* Loading skeleton */}
        {isPageLoading ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start mt-2">
            <div className="xl:col-span-1">
              <DashboardCard className="flex flex-col gap-5 p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white">
                <div className="flex items-center gap-3">
                  <div className="h-[52px] w-[52px] rounded-xl bg-gray-100 animate-pulse" />
                  <div className="flex flex-col gap-2">
                    <div className="h-5 w-36 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-20 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
                <div className="flex flex-col gap-3 mt-2">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="h-4 w-full bg-gray-100 rounded animate-pulse"
                    />
                  ))}
                </div>
              </DashboardCard>
            </div>
            <div className="xl:col-span-2">
              <TableSkeleton columns={5} rows={3} />
            </div>
          </div>
        ) : (
          <div className="grid min-w-0 grid-cols-1 items-start gap-4 sm:gap-6 xl:grid-cols-3">
            {/* Left Column: Integration Card */}
            <div className="xl:col-span-1 flex min-w-0 flex-col gap-4 sm:gap-6">
              <DashboardCard className="flex min-w-0 flex-col gap-5 rounded-[16px] border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex items-center gap-3">
                  <IntegrationLogo
                    type="google_workspace"
                    size="h-[52px] w-[52px]"
                  />
                  <div className="flex flex-col items-start gap-1">
                    <h2 className="text-base font-semibold text-gray-900 leading-tight">
                      Google Workspace
                    </h2>
                    <span className="text-[10px] font-bold text-[#1a73e8] bg-[#e8f0fe] px-2.5 py-0.5 rounded">
                      {activeHealthItem?.lifecycle_status_label ||
                        activeIntegration?.status_label ||
                        (isConnected ? 'Account Activated' : 'Disconnected')}
                    </span>
                  </div>
                </div>

                {/* Failure reason displayed under the badge when unhealthy */}
                {isUnhealthy && activeHealthItem?.failure_reason && (
                  <IntegrationAlertBanner
                    type="error"
                    title="Connection Alert"
                    message={activeHealthItem.failure_reason}
                  />
                )}

                {/* Account Details */}
                <div className="flex flex-col gap-4 text-sm mt-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm text-gray-400 font-medium">
                      Google Account
                    </span>
                    <span className="text-gray-900 text-base break-all">
                      {displayEmail || 'Not Connected'}
                    </span>
                  </div>

                  {showWorkspace && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm text-gray-400 font-medium">
                        Workspace
                      </span>
                      <span className="text-gray-900 text-base break-all">
                        {workspaceName}
                      </span>
                    </div>
                  )}
                </div>

                {/* Google Drive Storage Settings */}
                <div className="flex flex-col gap-4 mt-2 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0">
                      <path
                        fill="#FBBC05"
                        d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z"
                      />
                      <path
                        fill="#4285F4"
                        d="M22.85 15L16.29 3.5H9.15L15.71 15z"
                      />
                      <path fill="#34A853" d="M4.58 21h14.84l3.43-6H1.15z" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Google Drive Storage
                    </h3>
                  </div>
                  <div className="flex flex-col gap-3">
                    {/* Invoices toggle */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-600">
                          Save invoices to Drive
                        </span>
                        <Switch
                          checked={
                            isConnected &&
                            Boolean(statusData?.save_invoices_to_drive)
                          }
                          onChange={(val) =>
                            handleToggle('save_invoices_to_drive', val)
                          }
                          disabled={!isConnected || isUpdatingSettings}
                          aria-label="Toggle save invoices to Drive"
                        />
                      </div>
                      {syncPendingFor === 'invoices' && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-[12px] leading-relaxed">
                          <p className="text-blue-800 font-medium mb-2">
                            Sync existing invoices to Drive?
                          </p>
                          <p className="text-blue-600 mb-3">
                            New invoices will auto-upload going forward. Would
                            you also like to upload all past invoices to{' '}
                            <span className="font-semibold">
                              Aexon/Invoices/
                            </span>{' '}
                            now?
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              id="gw-sync-invoices-yes-btn"
                              type="button"
                              disabled={isSyncingExisting}
                              onClick={() =>
                                handleSyncExisting('invoices', true)
                              }
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold rounded-md transition-colors disabled:opacity-60"
                            >
                              {isSyncingExisting ? (
                                <svg
                                  className="w-3 h-3 animate-spin"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                >
                                  <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                                  <path d="M12 3a9 9 0 0 1 9 9" />
                                </svg>
                              ) : (
                                <svg
                                  className="w-3 h-3"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                  />
                                </svg>
                              )}
                              {isSyncingExisting
                                ? 'Syncing...'
                                : 'Yes, sync all'}
                            </button>
                            <button
                              id="gw-sync-invoices-skip-btn"
                              type="button"
                              disabled={isSyncingExisting}
                              onClick={() =>
                                handleSyncExisting('invoices', false)
                              }
                              className="px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-600 text-[11px] font-semibold rounded-md border border-gray-200 transition-colors disabled:opacity-60"
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quotations toggle */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-600">
                          Save quotations to Drive
                        </span>
                        <Switch
                          checked={
                            isConnected &&
                            Boolean(statusData?.save_quotations_to_drive)
                          }
                          onChange={(val) =>
                            handleToggle('save_quotations_to_drive', val)
                          }
                          disabled={!isConnected || isUpdatingSettings}
                          aria-label="Toggle save quotations to Drive"
                        />
                      </div>
                      {syncPendingFor === 'quotations' && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-[12px] leading-relaxed">
                          <p className="text-blue-800 font-medium mb-2">
                            Sync existing quotations to Drive?
                          </p>
                          <p className="text-blue-600 mb-3">
                            New quotations will auto-upload going forward. Would
                            you also like to upload all past quotations to{' '}
                            <span className="font-semibold">
                              Aexon/Quotations/
                            </span>{' '}
                            now?
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              id="gw-sync-quotations-yes-btn"
                              type="button"
                              disabled={isSyncingExisting}
                              onClick={() =>
                                handleSyncExisting('quotations', true)
                              }
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold rounded-md transition-colors disabled:opacity-60"
                            >
                              {isSyncingExisting ? (
                                <svg
                                  className="w-3 h-3 animate-spin"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                >
                                  <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                                  <path d="M12 3a9 9 0 0 1 9 9" />
                                </svg>
                              ) : (
                                <svg
                                  className="w-3 h-3"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                  />
                                </svg>
                              )}
                              {isSyncingExisting
                                ? 'Syncing...'
                                : 'Yes, sync all'}
                            </button>
                            <button
                              id="gw-sync-quotations-skip-btn"
                              type="button"
                              disabled={isSyncingExisting}
                              onClick={() =>
                                handleSyncExisting('quotations', false)
                              }
                              className="px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-600 text-[11px] font-semibold rounded-md border border-gray-200 transition-colors disabled:opacity-60"
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Lead Attachments toggle */}
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">
                        Save lead attachments to Drive
                      </span>
                      <Switch
                        checked={
                          isConnected &&
                          Boolean(statusData?.save_lead_attachments_to_drive)
                        }
                        onChange={(val) =>
                          handleToggle('save_lead_attachments_to_drive', val)
                        }
                        disabled={!isConnected || isUpdatingSettings}
                        aria-label="Toggle save lead attachments to Drive"
                      />
                    </div>
                  </div>
                </div>

                {/* Gmail Sync Status Banner */}
                {isConnected && statusData?.sync_preference === 'pending' && (
                  <div className="flex flex-col gap-2 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl mt-1">
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-4 h-4 text-amber-500 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-[12px] font-semibold text-amber-700">
                        Setup Incomplete
                      </p>
                    </div>
                    <button
                      onClick={() => setSyncModalOpen(true)}
                      className="text-[11px] font-semibold text-amber-600 bg-white border border-amber-200 hover:bg-amber-100 px-3 py-1.5 rounded-lg w-fit transition-colors"
                    >
                      Complete Gmail Setup
                    </button>
                  </div>
                )}
                {isConnected && statusData?.sync_status === 'running' && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl mt-1">
                    <svg
                      className="w-4 h-4 text-blue-500 animate-spin shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.15" />
                      <path strokeLinecap="round" d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-blue-700">
                        Importing your emails...
                      </p>
                      <p className="text-[11px] text-blue-500 mt-0.5">
                        This runs in the background. You can close this page.
                      </p>
                    </div>
                  </div>
                )}
                {isConnected &&
                  statusData?.sync_status === 'done' &&
                  statusData?.last_synced_at && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-100 rounded-xl mt-1">
                      <svg
                        className="w-4 h-4 text-green-500 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-[12px] font-medium text-green-700">
                        Gmail synced successfully
                      </p>
                    </div>
                  )}
                {isConnected && statusData?.sync_status === 'failed' && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-xl mt-1">
                    <svg
                      className="w-4 h-4 text-red-500 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <p className="text-[12px] font-medium text-red-700">
                      Email sync failed — try reconnecting
                    </p>
                  </div>
                )}

                {/* CTAs */}
                <IntegrationActionButtons
                  isConnected={isConnected}
                  isHealthy={!isUnhealthy}
                  isConnecting={isConnecting}
                  onConnect={handleConnect}
                  onDisconnect={() => setDisconnectOpen(true)}
                  connectLabel="Sign in with Google"
                  connectIcon={
                    <svg
                      viewBox="0 0 24 24"
                      className="w-4 h-4 shrink-0"
                      fill="currentColor"
                    >
                      <path
                        fill="#fff"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="rgba(255,255,255,0.85)"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="rgba(255,255,255,0.6)"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                      />
                      <path
                        fill="rgba(255,255,255,0.9)"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  }
                  primaryColorClass="bg-[#4285F4] hover:bg-[#3367d6] text-white"
                />
              </DashboardCard>
            </div>

            {/* Right Column: Health Status Table & Features */}
            <div className="flex min-w-0 flex-col gap-4 sm:gap-6 xl:col-span-2">
              {/* Health Table matching WhatsApp / Instagram UI */}
              {isHealthSkeleton ? (
                <TableSkeleton columns={5} rows={3} />
              ) : isHealthError ? (
                <div className="w-full p-8 bg-red-500/5 border border-red-500/10 rounded-2xl flex flex-col items-center justify-center gap-3 text-center">
                  <div className="text-red-500 text-sm font-semibold">
                    Failed to fetch integration health status
                  </div>
                  <p className="text-xs text-text-muted max-w-sm">
                    {healthError?.response?.data?.detail ||
                      healthError?.message ||
                      'Error occurred while contacting the health endpoint.'}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => refetchHealth()}
                    className="py-2 px-5 h-[38px] text-xs"
                  >
                    Retry Check
                  </Button>
                </div>
              ) : (
                <>
                  <div className="min-w-0 rounded-[12px] border border-gray-100 bg-white p-4 md:hidden">
                    {[
                      ['Integration Name', activeHealthItem.integration_name],
                      ['Integration Slug', activeHealthItem.integration_slug],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="flex min-w-0 flex-col gap-0.5 border-b border-gray-100 py-3 first:pt-0"
                      >
                        <span className="text-[11px] font-medium text-gray-400">
                          {label}
                        </span>
                        <span className="break-all text-[13px] font-medium text-gray-900">
                          {value}
                        </span>
                      </div>
                    ))}
                    <div className="flex min-w-0 flex-col gap-1 border-b border-gray-100 py-3">
                      <span className="text-[11px] font-medium text-gray-400">
                        Lifecycle Status
                      </span>
                      <span
                        className={`w-max max-w-full break-words px-3 py-1 text-[11px] font-semibold rounded ${
                          (
                            activeHealthItem.lifecycle_status || ''
                          ).toLowerCase() === 'activated' || isConnected
                            ? 'text-primary bg-primary/10'
                            : 'text-amber-600 bg-amber-50'
                        }`}
                      >
                        {activeHealthItem.lifecycle_status_label ||
                          activeHealthItem.lifecycle_status ||
                          'Account Activated'}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-col gap-1 border-b border-gray-100 py-3">
                      <span className="text-[11px] font-medium text-gray-400">
                        Status
                      </span>
                      <span
                        className={`w-max max-w-full break-words px-3 py-1 text-[11px] font-semibold rounded ${
                          isHealthy
                            ? 'text-primary bg-primary/10'
                            : 'text-red-500 bg-red-50'
                        }`}
                      >
                        {activeHealthItem.health_status_label ||
                          activeHealthItem.health_status ||
                          'Unhealthy'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 pt-3">
                      <span className="text-[11px] font-medium text-gray-400">
                        Action
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() =>
                            navigate('/integrations/google-workspace/history')
                          }
                          title="View Health History"
                          className="text-orange-500 hover:opacity-70 transition-opacity"
                        >
                          <svg
                            className="w-[18px] h-[18px]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </button>
                        <button
                          disabled={isHealthy}
                          onClick={() => setRequestDrawerOpen(true)}
                          title={
                            isHealthy
                              ? 'Integration is Healthy'
                              : 'View Integration Requests'
                          }
                          className={`transition-opacity ${isHealthy ? 'text-purple-500 opacity-40 cursor-not-allowed' : 'text-purple-500 hover:opacity-70'}`}
                        >
                          <svg
                            className="w-[18px] h-[18px]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <DashboardTableWrapper className="hidden min-w-0 md:block">
                    <DashboardTable className="w-full min-w-[640px]">
                      <DashboardTableHeader>
                        <th className="py-3 px-6 text-left font-medium">
                          Integration Name
                        </th>
                        <th className="py-3 px-6 text-left font-medium">
                          Integration Slug
                        </th>
                        <th className="py-3 px-6 text-center font-medium">
                          Lifecycle Status
                        </th>
                        <th className="py-3 px-6 text-center font-medium">
                          Status
                        </th>
                        <th className="py-3 px-6 text-center font-medium w-24">
                          Action
                        </th>
                      </DashboardTableHeader>
                      <DashboardTableBody>
                        <DashboardTableRow className="border-b border-gray-50 last:border-none">
                          <td className="py-4 px-6 text-[13px] text-gray-900 font-medium">
                            {activeHealthItem.integration_name}
                          </td>
                          <td className="py-4 px-6 text-[13px] text-gray-900">
                            {activeHealthItem.integration_slug}
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span
                              className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
                                (
                                  activeHealthItem.lifecycle_status || ''
                                ).toLowerCase() === 'activated' || isConnected
                                  ? 'text-primary bg-primary/10'
                                  : 'text-amber-600 bg-amber-50'
                              }`}
                            >
                              {activeHealthItem.lifecycle_status_label ||
                                activeHealthItem.lifecycle_status ||
                                'Account Activated'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span
                              className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
                                isHealthy
                                  ? 'text-primary bg-primary/10'
                                  : 'text-red-500 bg-red-50'
                              }`}
                            >
                              {activeHealthItem.health_status_label ||
                                activeHealthItem.health_status ||
                                'Unhealthy'}
                            </span>
                          </td>
                          <td
                            className="py-4 px-6 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() =>
                                  navigate(
                                    '/integrations/google-workspace/history',
                                  )
                                }
                                title="View Health History"
                                className="text-orange-500 hover:opacity-70 transition-opacity"
                              >
                                <svg
                                  className="w-[18px] h-[18px]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </button>
                              <button
                                disabled={isHealthy}
                                onClick={() => setRequestDrawerOpen(true)}
                                title={
                                  isHealthy
                                    ? 'Integration is Healthy'
                                    : 'View Integration Requests'
                                }
                                className={`transition-opacity ${
                                  isHealthy
                                    ? 'text-purple-500 opacity-40 cursor-not-allowed'
                                    : 'text-purple-500 hover:opacity-70'
                                }`}
                              >
                                <svg
                                  className="w-[18px] h-[18px]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </DashboardTableRow>
                      </DashboardTableBody>
                    </DashboardTable>
                  </DashboardTableWrapper>
                </>
              )}

              <DashboardCard className="flex min-w-0 flex-col gap-4 rounded-[16px] border border-gray-100 bg-white p-4 shadow-sm sm:gap-5 sm:p-5">
                <h3 className="text-[13px] font-semibold text-gray-700 uppercase tracking-wide">
                  {isConnected ? 'Active Permissions' : "What You'll Unlock"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {FEATURE_CARDS.map((feature) => {
                    const scopeMeta = SCOPE_LABELS.find(
                      (s) =>
                        feature.scopeKey
                          .toLowerCase()
                          .includes(s.key.toLowerCase()) ||
                        s.key
                          .toLowerCase()
                          .includes(feature.scopeKey.toLowerCase()),
                    );
                    const isGranted =
                      !isConnected ||
                      scopes.length === 0 ||
                      scopes.some((scopeStr) => {
                        const s = String(scopeStr).toLowerCase();
                        if (feature.scopeKey === 'gmail') {
                          return (
                            s.includes('gmail.modify') ||
                            s.includes('mail.google.com')
                          );
                        }
                        return s.includes(feature.scopeKey.toLowerCase());
                      });

                    return (
                      <div
                        key={feature.title}
                        className={`flex gap-3 p-4 rounded-xl border ${
                          isGranted
                            ? feature.bg
                            : 'bg-gray-50 border-gray-100 opacity-60'
                        }`}
                      >
                        <div className="shrink-0 mt-0.5">{scopeMeta?.icon}</div>
                        <div>
                          <p className="text-[13px] font-semibold text-gray-800">
                            {feature.title}
                          </p>
                          <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">
                            {feature.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DashboardCard>

              {/* Security notice */}
              {/* <DashboardCard className="p-4 shadow-sm border border-amber-100 bg-amber-50 rounded-[16px] flex gap-3">
                <svg
                  className="w-4 h-4 text-amber-500 shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
                <p className="text-[12px] text-amber-700 leading-relaxed">
                  <strong>Secure &amp; Private</strong> — OAuth tokens are
                  encrypted at rest and never exposed to the frontend. Live
                  health is monitored periodically to detect token expiry or
                  revocation.
                </p>
              </DashboardCard> */}
            </div>
          </div>
        )}
      </DashboardPage>

      {/* Reconnect Request Drawer (same as WA & IG) */}
      <RequestDrawer
        integration={appForDrawer}
        companyIntegrationId={companyIntegrationId}
        isOpen={requestDrawerOpen}
        onClose={() => setRequestDrawerOpen(false)}
      />
    </>
  );
}

export default GoogleWorkspaceDetailPage;
